import createHttp from '@/config/http';
import { createAudit } from '@/services/audit';
import type { CreateAuditDto } from '@/types/audit';

const http = createHttp(
	import.meta.env.VITE_BGCRON_URL || 'http://localhost:3011',
);

/**
 * Get user audit data from localStorage
 */
const getAuditUser = () => {
	const auditUserStr = localStorage.getItem('auditUser');
	if (auditUserStr) {
		try {
			return JSON.parse(auditUserStr);
		} catch (error) {
			return {};
		}
	}
	return {};
};

/**
 * Create audit log for bg-process and return audit ID
 */
const createBgProcessAudit = async (url: string, method: string, payload: any): Promise<number | undefined> => {
	const auditUser = getAuditUser();
	
	const auditData: CreateAuditDto = {
		originalUrl: url,
		originalPayload: JSON.stringify(payload),
		originalMethod: method,
	};

	// Add user data if available
	if (auditUser.nUserId) auditData.nUserId = auditUser.nUserId;
	if (auditUser.userEmail) auditData.userEmail = auditUser.userEmail;
	if (auditUser.fullName) auditData.fullName = auditUser.fullName;
	if (auditUser.port) auditData.Port = auditUser.port;
	if (auditUser.ipAddress) auditData.ipAddress = auditUser.ipAddress;
	if (auditUser.state) auditData.state = auditUser.state;
	if (auditUser.country) auditData.country = auditUser.country;
	if (auditUser.city) auditData.city = auditUser.city;
	if (auditUser.userType) auditData.userType = auditUser.userType;
	if (auditUser.userClinicIds) auditData.userClinicIds = auditUser.userClinicIds;
	if (auditUser.userClinicNames) auditData.userClinicNames = auditUser.userClinicNames;

	try {
		if (method.toLowerCase() === 'post' && window.location.pathname === '/') {
			auditData.FEurl = '/';
		}
		const auditResponse = await createAudit(auditData);
		return auditResponse?.auditId;
	} catch (error) {
		console.warn('Failed to create audit for bg-process:', error);
		return undefined;
	}
};

export const getAllBgProcess = async (args: { clinicIds: string }) => {
	const { clinicIds } = args;
	const response = await http.get(`/bg-process/all?clinicIds=${clinicIds}`);
	return response.data;
};

export const createBgProcess = async (args: {
	moduleName: string;
	userId: string;
	clinicId?: string;
	payload: { payload: any; additionalPayload?: any };
}) => {
	const { moduleName, userId, payload, clinicId } = args;

	// Create audit first and get audit ID
	const url = `/bg-process/start/${moduleName}`;
	const auditId = await createBgProcessAudit(url, 'POST', payload);

	// Call bg-process with auditId
	const queryParams = new URLSearchParams({
		userId,
		...(clinicId && { clinicId }),
		...(auditId && { auditId: auditId.toString() }),
	});

	const response = await http.post(
		`/bg-process/start/${moduleName}?${queryParams}`,
		payload,
	);

	return response.data;
};

export const updateBgProcess = async (args: {
	moduleName: string;
	userId: string;
	crmId?: string;
	clinicId?: string;
	flag?: number;
	payload: { payload: any; additionalPayload?: any };
}) => {
	const { moduleName, userId, crmId, payload, flag, clinicId } = args;

	// Create audit first and get audit ID
	const url = `/bg-process/start/${moduleName}/${crmId}`;
	const auditId = await createBgProcessAudit(url, 'PATCH', payload);

	// Call bg-process with auditId
	const queryParams = new URLSearchParams({
		userId,
		...(clinicId && { clinicId }),
		...(flag !== undefined && { flag: flag.toString() }),
		...(auditId && { auditId: auditId.toString() }),
	});

	const response = await http.patch(
		`/bg-process/start/${moduleName}/${crmId}?${queryParams}`,
		payload,
	);

	return response.data;
};
